"use client";

import { useState, useEffect } from "react";
import { FiX, FiBell, FiArrowUp, FiArrowDown, FiTrash2 } from "react-icons/fi";
import { authFetch } from "@/lib/authFetch";
import { createLogger } from "@/lib/logger";
import { useT } from "@/hooks/useT";

const logger = createLogger("PriceAlertModal");

interface ExistingAlert {
  id: string;
  outcome: "yes" | "no";
  condition: "above" | "below";
  target_price: number;
}

interface PriceAlertModalProps {
  isOpen: boolean;
  onClose: () => void;
  marketTitle: string;
  marketSlug: string;
  eventSlug?: string | null;
  marketId: string;
  tokenIds: string[];
  outcomes: string[];
  currentPrices: number[];
  onCreated?: () => void;
  source?: 'polymarket' | 'kalshi';
  marketImage?: string | null;
}

export default function PriceAlertModal({
  isOpen,
  onClose,
  marketTitle,
  marketSlug,
  eventSlug,
  marketId,
  tokenIds,
  outcomes,
  currentPrices,
  onCreated,
  source = 'polymarket',
  marketImage,
}: PriceAlertModalProps) {
  const t = useT();
  const [outcome, setOutcome] = useState<"yes" | "no">("yes");
  const [condition, setCondition] = useState<"above" | "below">("above");
  const [targetPrice, setTargetPrice] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);

  // Existing alerts for this market
  const [existingAlerts, setExistingAlerts] = useState<ExistingAlert[]>([]);
  const [loadingAlerts, setLoadingAlerts] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  // Fetch existing alerts when modal opens
  useEffect(() => {
    if (!isOpen) return;
    const fetchAlerts = async () => {
      setLoadingAlerts(true);
      try {
        const res = await authFetch(`/api/price-alerts?source=${source}`);
        if (res.ok) {
          const data = await res.json();
          const alerts = (data.data?.alerts || []) as any[];
          // Filter by market_id
          const forMarket = alerts.filter((a: any) => a.market_id === marketId);
          setExistingAlerts(forMarket);
        }
      } catch {
        // Silent fail
      } finally {
        setLoadingAlerts(false);
      }
    };
    fetchAlerts();
  }, [isOpen, marketId, source]);

  const handleDelete = async (alertId: string) => {
    setDeletingId(alertId);
    try {
      const res = await authFetch(`/api/price-alerts/${alertId}`, { method: "DELETE" });
      if (res.ok) {
        setExistingAlerts((prev) => prev.filter((a) => a.id !== alertId));
      }
    } catch {
      // Silent fail
    } finally {
      setDeletingId(null);
    }
  };

  if (!isOpen) return null;

  const yesPrice = currentPrices[0] ?? 0;
  const noPrice = currentPrices[1] ?? 0;
  const selectedPrice = outcome === "yes" ? yesPrice : noPrice;
  const selectedTokenId = outcome === "yes" ? tokenIds[0] : tokenIds[1];

  const handleSubmit = async () => {
    const cents = parseFloat(targetPrice);
    if (isNaN(cents) || cents < 1 || cents > 99) {
      setError(t("priceAlert.errorRange"));
      return;
    }
    const currentCents = selectedPrice * 100;
    if (condition === "above" && cents <= currentCents) {
      setError(t("priceAlert.errorTargetAbove", { current: currentCents.toFixed(1) }));
      return;
    }
    if (condition === "below" && cents >= currentCents) {
      setError(t("priceAlert.errorTargetBelow", { current: currentCents.toFixed(1) }));
      return;
    }
    const price = cents / 100;

    setSaving(true);
    setError("");

    try {
      const payload = {
        market_id: marketId,
        market_slug: marketSlug,
        market_title: marketTitle,
        event_slug: eventSlug || null,
        token_id: selectedTokenId,
        outcome,
        condition,
        target_price: price,
        source,
      };
      const controller = new AbortController();
      const timeout = setTimeout(() => controller.abort(), 10_000);
      const res = await authFetch("/api/price-alerts", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
        signal: controller.signal,
      });
      clearTimeout(timeout);

      if (res.ok) {
        const data = await res.json().catch(() => ({}));
        if (data.data?.alert) {
          setExistingAlerts((prev) => [...prev, data.data.alert]);
        }
        setSuccess(true);
        onCreated?.();
        setTimeout(() => {
          onClose();
          setSuccess(false);
          setTargetPrice("");
        }, 1200);
      } else {
        const data = await res.json().catch(() => ({}));
        setError(data.error || t("priceAlert.errorCreate"));
      }
    } catch (err) {
      if (err instanceof DOMException && err.name === 'AbortError') {
        setError(t("priceAlert.errorTimeout"));
      } else {
        setError(t("priceAlert.errorCreate"));
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[500] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-md bg-white dark:bg-[#18181D] rounded-[3px] border border-gray-200 dark:border-[#1A1A1A] shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-[#1A1A1A]">
          <div className="flex items-center gap-2">
            <FiBell className="w-5 h-5 text-amber-500" />
            <h2 className="font-inter-tight font-bold text-base text-gray-900 dark:text-white">{t("priceAlert.title")}</h2>
          </div>
          <button onClick={onClose} className="p-1.5 hover:bg-gray-100 dark:hover:bg-[#25252B] rounded-[3px] transition-colors">
            <FiX className="w-4 h-4 text-gray-500" />
          </button>
        </div>

        <div className="p-4 space-y-4">
          {/* Market title */}
          <div className="flex items-center gap-2.5">
            {marketImage && (
              <img src={marketImage} alt="" className="w-8 h-8 rounded-[3px] object-cover flex-shrink-0" onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }} />
            )}
            <p className="text-sm text-gray-600 dark:text-gray-400 line-clamp-2">{marketTitle}</p>
          </div>

          {/* Existing alerts for this market */}
          {loadingAlerts ? (
            <div className="flex items-center gap-2 text-xs text-gray-400">
              <div className="w-3 h-3 border border-gray-400 border-t-transparent rounded-full animate-spin" />
              {t("priceAlert.loadingAlerts")}
            </div>
          ) : existingAlerts.length > 0 && (
            <div className="space-y-2">
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">{t("priceAlert.activeAlerts", { count: existingAlerts.length })}</p>
              <div className="space-y-1.5">
                {existingAlerts.map((alert) => (
                  <div
                    key={alert.id}
                    className="flex items-center justify-between px-3 py-2 rounded-[3px] bg-gray-50 dark:bg-[#0a0a0b] border border-gray-200 dark:border-[#1A1A1A]"
                  >
                    <div className="flex items-center gap-2 text-xs">
                      <span className={`font-semibold ${alert.outcome === "yes" ? "text-emerald-500" : "text-red-400"}`}>
                        {(alert.outcome || "").toUpperCase()}
                      </span>
                      <span className="text-gray-400">
                        {alert.condition === "above" ? "↑" : "↓"} {(alert.target_price * 100).toFixed(1)}¢
                      </span>
                    </div>
                    <button
                      onClick={() => handleDelete(alert.id)}
                      disabled={deletingId === alert.id}
                      className="p-1 text-gray-400 hover:text-red-500 transition-colors disabled:opacity-50"
                    >
                      {deletingId === alert.id ? (
                        <div className="w-3.5 h-3.5 border border-gray-400 border-t-transparent rounded-full animate-spin" />
                      ) : (
                        <FiTrash2 className="w-3.5 h-3.5" />
                      )}
                    </button>
                  </div>
                ))}
              </div>
              <div className="border-t border-gray-200 dark:border-[#1A1A1A] pt-3 mt-3">
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">{t("priceAlert.createNewAlert")}</p>
              </div>
            </div>
          )}

          {/* Outcome selector */}
          <div>
            <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">{t("priceAlert.outcome")}</label>
            <div className="grid grid-cols-2 gap-2">
              {outcomes.map((o, i) => {
                const key = i === 0 ? "yes" : "no";
                const price = currentPrices[i] ?? 0;
                return (
                  <button
                    key={key}
                    onClick={() => setOutcome(key as "yes" | "no")}
                    className={`flex items-center justify-between px-3 py-2.5 rounded-[3px] border text-sm font-medium transition-all ${
                      outcome === key
                        ? key === "yes"
                          ? "bg-emerald-500/10 border-emerald-500/40 text-emerald-600 dark:text-emerald-400"
                          : "bg-red-500/10 border-red-500/40 text-red-600 dark:text-red-400"
                        : "bg-gray-50 dark:bg-[#0a0a0b] border-gray-200 dark:border-[#1A1A1A] text-gray-600 dark:text-gray-400"
                    }`}
                  >
                    <span>{o || (key === "yes" ? t("priceAlert.yes") : t("priceAlert.no"))}</span>
                    <span className="font-mono text-xs tabular-nums">{(price * 100).toFixed(1)}¢</span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Condition selector */}
          <div>
            <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">{t("priceAlert.alertWhen")}</label>
            <div className="grid grid-cols-2 gap-2">
              <button
                onClick={() => setCondition("above")}
                className={`flex items-center justify-center gap-1.5 px-3 py-2.5 rounded-[3px] border text-sm font-medium transition-all ${
                  condition === "above"
                    ? "bg-emerald-500/10 border-emerald-500/40 text-emerald-600 dark:text-emerald-400"
                    : "bg-gray-50 dark:bg-[#0a0a0b] border-gray-200 dark:border-[#1A1A1A] text-gray-600 dark:text-gray-400"
                }`}
              >
                <FiArrowUp className="w-4 h-4" />
                {t("priceAlert.above")}
              </button>
              <button
                onClick={() => setCondition("below")}
                className={`flex items-center justify-center gap-1.5 px-3 py-2.5 rounded-[3px] border text-sm font-medium transition-all ${
                  condition === "below"
                    ? "bg-red-500/10 border-red-500/40 text-red-600 dark:text-red-400"
                    : "bg-gray-50 dark:bg-[#0a0a0b] border-gray-200 dark:border-[#1A1A1A] text-gray-600 dark:text-gray-400"
                }`}
              >
                <FiArrowDown className="w-4 h-4" />
                {t("priceAlert.below")}
              </button>
            </div>
          </div>

          {/* Target price input */}
          <div>
            <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">{t("priceAlert.targetPrice")}</label>
            <div className="relative">
              <input
                type="number"
                step="0.1"
                min={condition === "above" ? Math.ceil((selectedPrice * 100 + 0.1) * 10) / 10 : 1}
                max={condition === "below" ? Math.floor((selectedPrice * 100 - 0.1) * 10) / 10 : 99}
                value={targetPrice}
                onChange={(e) => {
                  const val = e.target.value;
                  setTargetPrice(val);
                  const cents = parseFloat(val);
                  const currentCents = selectedPrice * 100;
                  if (val && !isNaN(cents)) {
                    if (condition === "above" && cents <= currentCents) {
                      setError(t("priceAlert.errorTargetAbove", { current: currentCents.toFixed(1) }));
                    } else if (condition === "below" && cents >= currentCents) {
                      setError(t("priceAlert.errorTargetBelow", { current: currentCents.toFixed(1) }));
                    } else {
                      setError("");
                    }
                  } else {
                    setError("");
                  }
                }}
                placeholder={condition === "above"
                  ? (Math.ceil((selectedPrice * 100 + 1) * 10) / 10).toFixed(1)
                  : (Math.floor((selectedPrice * 100 - 1) * 10) / 10).toFixed(1)
                }
                className="w-full pl-4 pr-8 py-2.5 text-sm tabular-nums border border-gray-200 dark:border-[#1A1A1A] rounded-[3px] focus:border-amber-500 focus:ring-2 focus:ring-amber-500/20 outline-none bg-white dark:bg-[#0a0a0b] text-gray-900 dark:text-white placeholder-gray-400"
              />
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-gray-400">¢</span>
            </div>
            <p className="text-xs text-gray-400 mt-1">
              {condition === "above"
                ? t("priceAlert.helperAbove", {
                    current: (selectedPrice * 100).toFixed(1),
                    min: (Math.ceil((selectedPrice * 100 + 0.1) * 10) / 10).toFixed(1),
                  })
                : t("priceAlert.helperBelow", {
                    current: (selectedPrice * 100).toFixed(1),
                    max: (Math.floor((selectedPrice * 100 - 0.1) * 10) / 10).toFixed(1),
                  })
              }
            </p>
          </div>

          {/* Error */}
          {error && (
            <p className="text-xs text-red-500 bg-red-500/10 border border-red-500/20 rounded-[3px] px-3 py-2">{error}</p>
          )}

          {/* Success */}
          {success && (
            <p className="text-xs text-emerald-500 bg-emerald-500/10 border border-emerald-500/20 rounded-[3px] px-3 py-2">
              {t("priceAlert.success")}
            </p>
          )}

          {/* Submit */}
          <button
            onClick={handleSubmit}
            disabled={saving || success || !targetPrice || !!error}
            className="w-full py-3 bg-amber-500 text-white font-inter-tight font-bold text-sm rounded-[3px] hover:bg-amber-600 disabled:opacity-50 transition-all flex items-center justify-center gap-2"
          >
            {saving ? (
              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
            ) : (
              <FiBell className="w-4 h-4" />
            )}
            {saving ? t("priceAlert.creating") : t("priceAlert.createAlert")}
          </button>
        </div>
      </div>
    </div>
  );
}
